import React from 'react';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { PanelShell } from '../ui/PanelShell';
import { SectionTitle } from '../ui/SectionTitle';
import { PillButton } from '../ui/PillButton';
import type { SupportState } from '../../types/tools';
import type { PrepareStep } from '../../types/prepare';
type OneClickPanelProps = {
  onClose: () => void;
  supportState: SupportState;
  prepareStep: PrepareStep;
  touchpoints: number;
  onRun: () => void;
};
const steps: {
  id: PrepareStep;
  label: string;
}[] = [{
  id: 'orienting',
  label: 'Auto-orient models'
}, {
  id: 'supporting',
  label: 'Generate supports'
}, {
  id: 'layout',
  label: 'Lay out on build platform'
}];
const order: PrepareStep[] = ['idle', 'orienting', 'supporting', 'layout', 'done'];
export function OneClickPanel({
  onClose,
  supportState,
  prepareStep,
  touchpoints,
  onRun
}: OneClickPanelProps) {
  const running = supportState === 'generating';
  const current = order.indexOf(prepareStep);
  return <PanelShell title="1-Click Print" onClose={onClose} footer={<PillButton variant="primary" onClick={onRun} disabled={running} data-id="element-400" data-mp="3pw0xea6dwfnt">
          {running && <Loader2Icon className="h-3 w-3 animate-spin" data-id="element-401" data-mp="3uuvjbkznekc5" />}
          {running ? 'Preparing…' : prepareStep === 'done' ? 'Prepare Again' : 'Prepare All'}
        </PillButton>} data-id="element-399" data-mp="3ptxz72v2vg80">
      
      <SectionTitle data-id="element-402" data-mp="7pcy50y0mmq2">Prepare Job</SectionTitle>
      <p className="pb-[8px] text-[12px] leading-snug text-pf-muted" data-id="element-403" data-mp="cno6t8ckqr6p">
        Orients the model with its detailed side facing the platform, generates supports and a raft, then centres it on the build platform. Saved protected areas are turned away from the platform and kept free of supports.
      </p>
      <ol className="flex flex-col gap-[7px] pb-[4px]" data-id="element-404" data-mp="qf2nv9mx8ib4">
        {steps.map(s => {
        const idx = order.indexOf(s.id);
        const done = current > idx;
        const active = current === idx;
        return <li key={s.id} className={`flex items-center gap-2 text-[12px] ${active ? 'font-semibold text-pf-ink' : done ? 'text-pf-ink' : 'text-pf-muted'}`} data-id="element-405" data-mp="10dij9oszedqf">
              <span className={`flex h-[14px] w-[14px] items-center justify-center rounded-full border transition-colors duration-150 ${done ? 'border-[#22b922] bg-[#22b922] text-white' : active ? 'border-pf-blue text-pf-blue' : 'border-pf-line bg-white'}`} data-id="element-406" data-mp="2rh25wde8prft">
                
                {done && <CheckIcon className="h-[9px] w-[9px]" strokeWidth={3} data-id="element-407" data-mp="3f51x1efgrjux" />}
                {active && <Loader2Icon className="h-[9px] w-[9px] animate-spin" strokeWidth={3} data-id="element-408" data-mp="3b3q5h3d14ok3" />}
              </span>
              {s.label}
            </li>;
      })}
      </ol>
      {prepareStep === 'done' && <p className="mt-[6px] rounded-[4px] bg-white px-2 py-[6px] text-[11.5px] text-pf-ink ring-1 ring-pf-line" data-id="element-409" data-mp="3k6hlzhkriail">
          Ready to print · <span className="font-semibold" data-id="element-410" data-mp="1usm67ohf21oz">{touchpoints}</span> touchpoints generated
        </p>}
    </PanelShell>;
}